import { useEffect, useRef } from 'react';
import { useAppDispatch } from '../../store/hooks';
import { initializeAuth } from '../../store/slices/authSlice';
import { useLocation } from 'react-router-dom';
import { getValidAuthToken, isTokenExpired, hydrateAuthFromSearchParams } from '../../utils/api/auth';
import { LOGIN_URL } from '../../utils/api/config';
import { isPublicAppRoute } from '../../utils/publicRoutes';
import { TRIAL_EXPIRED_LOGIN_MODAL_KEY } from '../../utils/companyBillingGate';
import { PLAN_RENEWAL_REMINDER_DISMISSED_KEY } from '../../utils/subscriptionRenewal';

const TRIAL_WELCOME_MODAL_KEY = 'showTrialWelcomeModal';

/**
 * Auth Initializer Component
 * Validates token on app load and redirects if expired
 */
export default function AuthInitializer() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const tokenHydratedRef = useRef(false);

  // Handle login redirect token in URL (runs when search params change).
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tokenFromUrl = urlParams.get('token');
    if (!tokenFromUrl) return;

    hydrateAuthFromSearchParams(location.search);
    sessionStorage.removeItem(PLAN_RENEWAL_REMINDER_DISMISSED_KEY);

    if (urlParams.get('trialWelcome') === '1') {
      sessionStorage.setItem(TRIAL_WELCOME_MODAL_KEY, '1');
    }

    if (
      urlParams.get('access_mode') === 'billing_only' ||
      urlParams.get('trial_expired') === '1'
    ) {
      sessionStorage.setItem(TRIAL_EXPIRED_LOGIN_MODAL_KEY, '1');
    }

    const newParams = new URLSearchParams(urlParams);
    newParams.delete('token');
    newParams.delete('name');
    newParams.delete('role');
    newParams.delete('email');
    newParams.delete('status');
    newParams.delete('access_mode');
    newParams.delete('trial_expired');
    newParams.delete('trialWelcome');

    const newUrl = newParams.toString()
      ? `${window.location.pathname}?${newParams.toString()}`
      : window.location.pathname;

    window.history.replaceState({}, '', newUrl);
    tokenHydratedRef.current = true;
  }, [location.search]);

  // Initialize auth + token expiry checks once on mount (not on every navigation).
  useEffect(() => {
    dispatch(initializeAuth());

    const isPublicRoute = isPublicAppRoute(window.location.pathname);
    if (isPublicRoute) {
      return undefined;
    }

    let checkTokenInterval = null;
    let timeout1 = null;
    let timeout2 = null;

    const checkToken = () => {
      const token = getValidAuthToken();
      const isExpired = token ? isTokenExpired(token) : true;

      if (!token || isExpired) {
        window.location.href = LOGIN_URL;
        return false;
      }

      return true;
    };

    const delayBeforeCheck = tokenHydratedRef.current ? 100 : 500;

    timeout1 = setTimeout(() => {
      if (!checkToken()) {
        return;
      }

      timeout2 = setTimeout(() => {
        if (!checkToken()) {
          return;
        }

        checkTokenInterval = setInterval(() => {
          if (!checkToken()) {
            if (checkTokenInterval) {
              clearInterval(checkTokenInterval);
            }
          }
        }, 10000);
      }, 1000);
    }, delayBeforeCheck);

    return () => {
      if (timeout1) clearTimeout(timeout1);
      if (timeout2) clearTimeout(timeout2);
      if (checkTokenInterval) clearInterval(checkTokenInterval);
    };
  }, [dispatch]);

  return null;
}
